class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.trendingAndProductsBlock = this.querySelector('[data-quick-trending-products]');
    this.closeStickySearchButton = document.querySelector('.header-search-close');
    this.productToShow = this.dataset.productToShow;
    this.isOpen = false;
    this.quickSearchTrending = this.querySelectorAll('.quickSearchTrending .link');

    this.setupEventListeners();
    this.quickSearchTrending.forEach(element => {
      element.addEventListener('click', this.quickSearchTrendingClick.bind(this));
    });
  }
  
  setupEventListeners() {
    const form = this.querySelector('form.search');
    if (form) {
      form.addEventListener('submit', this.onFormSubmit.bind(this));
    }
    
    if (this.input) {
      this.input.addEventListener('input', debounce((event) => {
        this.onChange(event);
      }, 500).bind(this));
      this.input.addEventListener('focus', this.onFocus.bind(this));
    }

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    document.addEventListener('click', this.onDocClick.bind(this));
    
    if (this.closeStickySearchButton) {
      this.closeStickySearchButton.addEventListener('click', this.onCloseStickySearchClick.bind(this));
    }
  }

  getSearchDetails() {
    return this.closest('.search_details') || document.querySelector('.search_details');
  }

  getQuery() {
    return this.input ? this.input.value.trim() : '';
  }

  onChange() {
    const searchTerm = this.getQuery();
    
    if (!searchTerm.length) {
      this.close(true);
      return;
    }
    
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();
    
    this.open();

    if (!searchTerm.length) return this.showTrendingAndProducts();

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }
  
  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
      this.showTrendingAndProducts();
    }
    event.preventDefault();
    
    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    
    const allElements = Array.from(
      this.querySelectorAll('li, [role="option"], .predictive-search__item, .card-wrapper, .product-item')
    );
    if (!allElements.length) return;

    let activeElement = allElements[0];

    if (moveUp && !selectedElement) return;

    if (this.statusElement) this.statusElement.textContent = '';
    
    if (!moveUp && selectedElement) {
      const currentIndex = allElements.indexOf(selectedElement);
      activeElement = allElements[currentIndex + 1] || allElements[0];
    } else if (moveUp) {
      const currentIndex = allElements.indexOf(selectedElement);
      activeElement = allElements[currentIndex - 1] || allElements[allElements.length - 1];
    }

    if (!activeElement || activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', 'true');
    if (selectedElement) selectedElement.setAttribute('aria-selected', 'false');

    if (!activeElement.id) {
      activeElement.id = `predictive-search-option-${Math.random().toString(36).substring(2, 9)}`;
    }

    activeElement.scrollIntoView({ block: 'nearest' });

    const titleElement = activeElement.querySelector('.card__heading, .product-item__title, .name, a') || activeElement;
    this.setLiveRegionText(titleElement.textContent.trim());
    if (this.input) {
      this.input.setAttribute('aria-activedescendant', activeElement.id);
    }
  }
    
  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');
    if (selectedProduct) selectedProduct.click();
  }
  
  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();
    
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      this.updateViewAllLink(searchTerm);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product&${encodeURIComponent('resources[limit]')}=${this.productToShow}&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        
        return response.text();
      })  
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
        this.updateViewAllLink(searchTerm);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }
  
  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    
    if (this.loadingText) this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', 'true');
  }

  setLiveRegionText(statusText) {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    if (!this.statusElement) return;

    this.statusElement.removeAttribute('aria-hidden');
    this.statusElement.textContent = statusText;
  }

  renderSearchResults(resultsMarkup) {
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
    }
    this.setAttribute('results', 'true');
    this.setAttribute('open', 'true');
    this.setLiveRegionResults();
    this.open();
    this.hideTrendingAndProducts();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const liveRegionCount = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegionCount) {
      this.setLiveRegionText(liveRegionCount.textContent);
    }
  }
  
  getResultsMaxHeight() {
    const nav = document.querySelector('[class^="header-navigation"]');
    this.resultsMaxHeight = nav ? window.innerHeight - nav.getBoundingClientRect().bottom : 300;
    return this.resultsMaxHeight;
  }
  
  open() {
    this.setAttribute('open', 'true');
    const details = this.getSearchDetails();
    if (details) {
      details.setAttribute('open', '');
      details.classList.add('is-open');
    }
    if (this.input) {
      this.input.setAttribute('aria-expanded', 'true');
    }
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm && this.input) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', 'false');

    if (this.input) {
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
    }

    this.removeAttribute('open');

    const details = this.getSearchDetails();
    if (details) {
      details.removeAttribute('open');
      details.classList.remove('is-open');
    }

    this.resultsMaxHeight = false;
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.removeAttribute('style');
    }

    this.isOpen = false;
  }

  showTrendingAndProducts() {
    if (!this.trendingAndProductsBlock) return;
    this.trendingAndProductsBlock.classList.add('is-show');
    this.trendingAndProductsBlock.classList.remove('hidden');
  }

  hideTrendingAndProducts() {
    if (!this.trendingAndProductsBlock) return;

    this.trendingAndProductsBlock.classList.remove('is-show');
    this.trendingAndProductsBlock.classList.add('hidden');
  }

  onDocClick(e) {
    const isInModal = this.contains(e.target);
    const $target = e.target;

    if (!isInModal || $target.closest('.header-search-popup-close') || $target.matches('.header-search-popup-close') || $target.closest('.header-search-close') || $target.matches('.header-search-close')) {
      this.close();
      this.hideTrendingAndProducts();
    }
  }

  onCloseStickySearchClick() {
    this.close();
    this.hideTrendingAndProducts();
    document.querySelector('body')?.classList.remove('sticky-search-open');
    document.querySelector('body')?.classList.remove('sticky-search-menu-open');
    this.closest('[class*="section-header-"]')?.classList.remove('sticky-search-menu-open');
    this.closest('[class*="section-header-"]')?.classList.remove('sticky-search-menu-custom-open');
  }

  updateViewAllLink(searchTerm) {
    const qsViewAllLink = document.querySelector('[data-qs-view-all-link]');
    if (!qsViewAllLink) return;

    const linkTotal = `${routes.search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product`;
    
    qsViewAllLink.href = linkTotal;
    return this.getTotalResults(linkTotal).then(count => {
      qsViewAllLink.innerHTML = qsViewAllLink.innerHTML.replace('()', `(${count})`);
    });
  }

  getTotalResults(url) {
    return fetch(url)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })  
      .then((text) => {
        const totalCount = new DOMParser().parseFromString(text, 'text/html').querySelector('[id^="SearchSection"]')?.dataset.searchCount;
        return totalCount || 0;
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  quickSearchTrendingClick(event) {
    window.location = event.currentTarget.getAttribute('href');
  }
}

customElements.define('predictive-search', PredictiveSearch);